import { colors } from '@cabonline/ui-shared';
import { Button, LinkButton, Text } from '@cabonline/ui-web';
import styled from '@emotion/styled';
import {
  AdminPanelSettingsOutlined,
  ApartmentOutlined,
  InfoOutlined,
  KeyboardArrowDown,
  LocalTaxiOutlined,
  MenuBookOutlined,
  PersonOutlineRounded,
  RouteOutlined,
} from '@mui/icons-material';
import { useAuth, useLocale } from '@multiplatform-apps/service-trips';
import { FC, ReactNode, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { paths } from '../../../constants';
import { ENABLE_ABSENCE_LIST, ENABLE_NOTIFICATIONS } from '../../../constants/feature-toggles';
import { useModal } from '../../../context';
import { useDeviceType, useIndexPermissions, usePermissions } from '../../../hooks';
import { COOKIE_CONSENT_MODAL } from '../../cookie-consent';
import { LanguageSelect } from '../../language-select';
import { NotificationBell } from '../notification-bell';
import {
  StyledAdminButton,
  StyledDropdown,
  StyledDropdownItem,
  StyledLanguageSelect,
  StyledLinkButtonWrapper,
  StyledLinkContainer,
  StyledNavLink,
} from './styles';

const StyledAdminDropdownWrapper = styled.div`
  position: relative;
`;

const StyledDropdownArrowWrapper = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  display: inline-flex;
  margin-left: 4px;
  transform: ${(p) => (p.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s;
`;

const StyledRouteIconWrapper = styled.span`
  display: inline-flex;
  transform: rotate(90deg);
`;

// ─── Generic hooks ────────────────────────────────────────────────────────────

const useOutsideReset = (ref: RefObject<HTMLElement | null>, onReset: () => void): void => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onReset();
    };
    const handleFocusIn = (event: FocusEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onReset();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [ref, onReset]);
};

const useDropdownClickOutside = (
  ref: RefObject<HTMLElement | null>,
  isOpen: boolean,
  onClose: () => void,
): void => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, isOpen, onClose]);
};

// ─── Sub-components ───────────────────────────────────────────────────────────

interface NavItemProps {
  to: string;
  icon: ReactNode;
  label: string;
  index: number;
  focusedIndex: number | null;
  onFocus: (index: number) => void;
}

const NavItem: FC<NavItemProps> = ({ to, icon, label, index, focusedIndex, onFocus }) => (
  <StyledNavLink
    to={to}
    onFocus={() => onFocus(index)}
    onClick={() => onFocus(index)}
    isFocused={focusedIndex === index}
  >
    {({ isActive }) => (
      <>
        {icon}
        <Text weight="extraBold" size="m" color={colors.white} underline={isActive} underlineOffset="xxs">
          {label}
        </Text>
      </>
    )}
  </StyledNavLink>
);

interface AdminDropdownProps {
  dropdownRef: RefObject<HTMLDivElement | null>;
  dropdownOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  t: (key: string) => string;
}

const AdminDropdown: FC<AdminDropdownProps> = ({ dropdownRef, dropdownOpen, onToggle, onClose, t }) => (
  <StyledAdminDropdownWrapper ref={dropdownRef}>
    <StyledAdminButton
      variant="secondary"
      onClick={onToggle}
      label={t('navBar.admin')}
      iconLeft={<AdminPanelSettingsOutlined htmlColor={colors.primary} />}
      iconRight={
        <StyledDropdownArrowWrapper isOpen={dropdownOpen}>
          <KeyboardArrowDown />
        </StyledDropdownArrowWrapper>
      }
      isDarkBackground
    />
    {dropdownOpen && (
      <StyledDropdown role="menu">
        <StyledDropdownItem to={paths.absence.root} onClick={onClose} role="menuitem">
          {t('absence.title')}
        </StyledDropdownItem>
        {ENABLE_ABSENCE_LIST && (
          <StyledDropdownItem to={paths.absence.list} onClick={onClose} role="menuitem">
            {t('absenceList.title')}
          </StyledDropdownItem>
        )}
      </StyledDropdown>
    )}
  </StyledAdminDropdownWrapper>
);

// ─── Hook ─────────────────────────────────────────────────────────────────────

const useDesktopNavbar = () => {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { isAuthenticated, identity } = useAuth();
  const { t } = useLocale();
  const { setModalOpen } = useModal();
  const navigate = useNavigate();
  const deviceType = useDeviceType();
  const indexPermissions = useIndexPermissions();
  const { hasRootRoutePermission: hasPermission } = usePermissions();
  const showAdmin = !!indexPermissions?.canSeeGeneralAbsenceMenuItem;
  const showTrips = hasPermission('tripsPage');
  const showPassengers = hasPermission('passengersPage');
  const showUsers = hasPermission('usersPage');
  const showInstitutions = hasPermission('institutionsPage');
  const showNotifications = ENABLE_NOTIFICATIONS && !!indexPermissions?.canSeeNotificationsMenuItem;

  const resetFocus = useCallback(() => setFocusedIndex(null), []);
  const closeDropdown = useCallback(() => setDropdownOpen(false), []);
  const toggleDropdown = useCallback(() => setDropdownOpen((o) => !o), []);
  const handleProfileClick = useCallback(() => {
    setFocusedIndex(null);
    navigate(paths.profile.root);
  }, [navigate]);

  useOutsideReset(navRef, resetFocus);
  useDropdownClickOutside(dropdownRef, dropdownOpen, closeDropdown);

  return {
    t,
    navRef,
    dropdownRef,
    focusedIndex,
    setFocusedIndex,
    dropdownOpen,
    toggleDropdown,
    closeDropdown,
    isAuthenticated,
    identity,
    showAdmin,
    showTrips,
    showPassengers,
    showUsers,
    showInstitutions,
    showNotifications,
    deviceType,
    setModalOpen,
    handleProfileClick,
    resetFocus,
  };
};

// ─── Primary nav link config (data-driven, avoids branching per item) ────────

interface PrimaryNavLink {
  key: string;
  to: string;
  icon: ReactNode;
  label: string;
  index: number;
  visible: boolean;
}

interface PrimaryNavLinkVisibility {
  showTrips: boolean;
  showPassengers: boolean;
  showUsers: boolean;
  showInstitutions: boolean;
}

const NAV_FOCUS_INDEX = {
  trips: 0,
  passengers: 1,
  users: 2,
  institutions: 3,
  manuals: 4,
  about: 5,
} as const;

const buildPrimaryNavLinks = (
  t: (key: string) => string,
  { showTrips, showPassengers, showUsers, showInstitutions }: PrimaryNavLinkVisibility,
): PrimaryNavLink[] => [
  {
    key: 'trips',
    to: paths.trips.root,
    icon: (
      <StyledRouteIconWrapper>
        <RouteOutlined htmlColor={colors.primary} />
      </StyledRouteIconWrapper>
    ),
    label: t('navBar.trips'),
    index: NAV_FOCUS_INDEX.trips,
    visible: showTrips,
  },
  {
    key: 'passengers',
    to: paths.passengers.root,
    icon: <LocalTaxiOutlined htmlColor={colors.primary} />,
    label: t('navBar.passengers'),
    index: NAV_FOCUS_INDEX.passengers,
    visible: showPassengers,
  },
  {
    key: 'users',
    to: paths.users.root,
    icon: <PersonOutlineRounded htmlColor={colors.primary} />,
    label: t('navBar.users'),
    index: NAV_FOCUS_INDEX.users,
    visible: showUsers,
  },
  {
    key: 'institutions',
    to: paths.institutions.root,
    icon: <ApartmentOutlined htmlColor={colors.primary} />,
    label: t('navBar.institutions'),
    index: NAV_FOCUS_INDEX.institutions,
    visible: showInstitutions,
  },
];

const getProfileLabel = (
  identity: ReturnType<typeof useAuth>['identity'],
  t: (key: string) => string,
): string => identity?.firstName || identity?.displayName || t('navBar.profile');

// ─── Component ────────────────────────────────────────────────────────────────

interface AuthenticatedNavLinksProps {
  t: (key: string) => string;
  identity: ReturnType<typeof useAuth>['identity'];
  focusedIndex: number | null;
  setFocusedIndex: (index: number) => void;
  resetFocus: () => void;
  dropdownRef: RefObject<HTMLDivElement | null>;
  dropdownOpen: boolean;
  showAdmin: boolean;
  showTrips: boolean;
  showPassengers: boolean;
  showUsers: boolean;
  showInstitutions: boolean;
  showNotifications: boolean;
  toggleDropdown: () => void;
  closeDropdown: () => void;
  handleProfileClick: () => void;
}

const AuthenticatedNavLinks: FC<AuthenticatedNavLinksProps> = ({
  t,
  identity,
  focusedIndex,
  setFocusedIndex,
  resetFocus,
  dropdownRef,
  dropdownOpen,
  showAdmin,
  showTrips,
  showPassengers,
  showUsers,
  showInstitutions,
  showNotifications,
  toggleDropdown,
  closeDropdown,
  handleProfileClick,
}) => {
  const primaryNavLinks = buildPrimaryNavLinks(t, {
    showTrips,
    showPassengers,
    showUsers,
    showInstitutions,
  });
  const profileLabel = getProfileLabel(identity, t);

  return (
    <>
      {primaryNavLinks
        .filter((link) => link.visible)
        .map((link) => (
          <NavItem
            key={link.key}
            to={link.to}
            icon={link.icon}
            label={link.label}
            index={link.index}
            focusedIndex={focusedIndex}
            onFocus={setFocusedIndex}
          />
        ))}
      {showAdmin && (
        <AdminDropdown
          dropdownRef={dropdownRef}
          dropdownOpen={dropdownOpen}
          onToggle={toggleDropdown}
          onClose={closeDropdown}
          t={t}
        />
      )}
      {showNotifications && <NotificationBell />}
      <Button
        variant="primary"
        testID="navbar-profile-page"
        label={profileLabel}
        aria-label={profileLabel}
        isDarkBackground
        onFocus={resetFocus}
        onClick={handleProfileClick}
        iconLeft={<PersonOutlineRounded aria-hidden={true} />}
      />
    </>
  );
};

interface UnauthenticatedFooterProps {
  t: (key: string) => string;
  onOpenCookieConsent: () => void;
}

const UnauthenticatedFooter: FC<UnauthenticatedFooterProps> = ({ t, onOpenCookieConsent }) => (
  <StyledLinkButtonWrapper>
    <LinkButton
      darkBackground
      onClick={onOpenCookieConsent}
      label={t('cookieConsent.cookieSettingsButton')}
      color={colors.white}
    />
  </StyledLinkButtonWrapper>
);

export const DesktopNavbar: FC = () => {
  const {
    t,
    navRef,
    dropdownRef,
    focusedIndex,
    setFocusedIndex,
    dropdownOpen,
    toggleDropdown,
    closeDropdown,
    isAuthenticated,
    identity,
    showAdmin,
    showTrips,
    showPassengers,
    showUsers,
    showInstitutions,
    showNotifications,
    deviceType,
    setModalOpen,
    handleProfileClick,
    resetFocus,
  } = useDesktopNavbar();

  const handleOpenCookieConsent = useCallback(
    () => setModalOpen(COOKIE_CONSENT_MODAL),
    [setModalOpen]
  );

  return (
    <>
      <StyledLanguageSelect>
        <LanguageSelect darkMode={deviceType === 'desktop'} id="desktop-language-select" />
      </StyledLanguageSelect>
      <StyledLinkContainer ref={navRef}>
        {isAuthenticated && (
          <AuthenticatedNavLinks
            t={t}
            identity={identity}
            focusedIndex={focusedIndex}
            setFocusedIndex={setFocusedIndex}
            resetFocus={resetFocus}
            dropdownRef={dropdownRef}
            dropdownOpen={dropdownOpen}
            showAdmin={showAdmin}
            showTrips={showTrips}
            showPassengers={showPassengers}
            showUsers={showUsers}
            showInstitutions={showInstitutions}
            showNotifications={showNotifications}
            toggleDropdown={toggleDropdown}
            closeDropdown={closeDropdown}
            handleProfileClick={handleProfileClick}
          />
        )}
        <NavItem
          to={paths.manuals.root}
          icon={<MenuBookOutlined htmlColor={colors.primary} />}
          label={t('navBar.manuals')}
          index={NAV_FOCUS_INDEX.manuals}
          focusedIndex={focusedIndex}
          onFocus={setFocusedIndex}
        />
        <NavItem
          to={paths.about.root}
          icon={<InfoOutlined htmlColor={colors.primary} />}
          label={t('navBar.about')}
          index={NAV_FOCUS_INDEX.about}
          focusedIndex={focusedIndex}
          onFocus={setFocusedIndex}
        />
      </StyledLinkContainer>
      {!isAuthenticated && (
        <UnauthenticatedFooter t={t} onOpenCookieConsent={handleOpenCookieConsent} />
      )}
    </>
  );
};
